import { zodResolver } from '@hookform/resolvers/zod';
import { PhoneNumberTextField } from '@montugroup/design-system';
import type { GetPatientDetailsInclude } from '@montugroup/pms-api-contracts';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import { Grid, InputAdornment } from '@mui/material';
import type { FieldValues } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { FormContainer, TextFieldElement } from 'react-hook-form-mui';
import type { z } from 'zod';
import FormSection from '../../FormSection';
import { PATIENT_DETAILS_FORM_NAMES } from '../../Patient.constants';
import { schema } from './EmergencyContact.schema';

export type EmergencyContactFormFields = z.infer<typeof schema>;

export type EmergencyContactProps = {
  editable?: boolean;
  defaultValues?: EmergencyContactFormFields;
  isLoading?: boolean;
};

export const EmergencyContact = ({ editable, defaultValues, isLoading }: EmergencyContactProps) => {
  const FORM_NAME: GetPatientDetailsInclude = PATIENT_DETAILS_FORM_NAMES.PATIENT_EMERGENCY_CONTACT;

  const formContext = useForm<EmergencyContactFormFields>({
    defaultValues,
    values: defaultValues,
    resolver: zodResolver(schema),
  });

  const {
    control,
    formState: { errors },
  } = formContext;

  const isEffectivelyEditable = editable;

  const transform = (dirtyValues: FieldValues): EmergencyContactFormFields => {
    return {
      firstName: dirtyValues?.firstName,
      lastName: dirtyValues?.lastName,
      relationship: dirtyValues?.relationship,
      phoneNumber: dirtyValues?.phoneNumber === null ? '' : dirtyValues?.phoneNumber,
    } as EmergencyContactFormFields;
  };

  return (
    <FormSection
      title="Emergency Contact"
      formName={FORM_NAME}
      formContext={formContext}
      isLoading={isLoading}
      transform={transform}
    >
      <FormContainer formContext={formContext}>
        <Grid container columnSpacing={10} rowSpacing={5} marginY={2} data-testid="emergency-contact-form">
          <Grid item xs={12} sm={12} lg={6}>
            <TextFieldElement name="firstName" label="First Name" fullWidth disabled={!isEffectivelyEditable} />
          </Grid>
          <Grid item xs={12} sm={12} lg={6}>
            <TextFieldElement name="lastName" label="Last Name" fullWidth disabled={!isEffectivelyEditable} />
          </Grid>
          <Grid item xs={12} sm={12} lg={6}>
            <TextFieldElement name="relationship" label="Relationship" fullWidth disabled={!isEffectivelyEditable} />
          </Grid>
          <Grid item xs={12} sm={12} lg={6}>
            <PhoneNumberTextField
              name="phoneNumber"
              label="Phone"
              fullWidth
              control={control}
              helperText={errors.phoneNumber?.message}
              variant={'outlined'}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIphoneIcon sx={{ color: (theme) => theme.palette.grey[500] }} />
                  </InputAdornment>
                ),
              }}
              disabled={!isEffectivelyEditable}
            />
          </Grid>
        </Grid>
      </FormContainer>
    </FormSection>
  );
};
